import { differenceBy } from "lodash-es";
import { type FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LoadingSpinner } from "widgets";

import { isRawDataFileTypeNfParam } from "../../../app-utils/playbook.js";
import { NfParamSubTypeEnum, NfParamTypeEnum, type NfWorkflowParam } from "../../../graphql.js";
import {
  fetchNfWorkflow,
  type IPlaybookConfigState,
  savePlaybook,
  updatePlaybookConfigState,
} from "../../../redux/modules/playbook-admin.js";
import { fetchFullSampleTypeWithAttributeDefaults } from "../../../redux/modules/sample-admin.js";
import { type IAppState } from "../../../redux/reducer.js";
import { type ReduxDispatch } from "../../../redux/redux-utils.js";
import { PLAYBOOK_TYPE } from "../../../settings/settings.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";
import NfParamsConfigurator, { getParamTypeLabel, type NfParamConfiguration } from "./NfParamsConfigurator.js";

type PlaybookConfigNfWorkflowInputScreenProps = IPlaybookWizardStepProvidedProps;

const PlaybookConfigNfWorkflowInputScreen: FC<PlaybookConfigNfWorkflowInputScreenProps> = (
  props: PlaybookConfigNfWorkflowInputScreenProps & IPlaybookWizardStepInjectedProps,
) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const configState = useSelector<IAppState, Partial<IPlaybookConfigState>>(
    (state) => state.playbookAdminState.playbookConfigState,
  );
  const hpcEnabled = useSelector<IAppState, boolean | null | undefined>(
    (state) => state.config.configuration?.application?.hpcEnabled,
  );
  const cloudEnabled = useSelector<IAppState, boolean | null | undefined>(
    (state) => state.config.configuration?.application?.cloudEnabled,
  );

  // const [nfParamConfigurations, setNfParamConfigurations] = useState<NfParamConfiguration[]>([]);
  // const [nfPlaybookParams, setNfPlaybookParams] = useState<InputNfParamInput[]>([]);
  // const [formError, setFormError] = useState<string>(null);
  const [isSavingPlaybook, setSavingPlaybook] = useState<boolean>(false);
  const [isLoadingData, setLoadingData] = useState<boolean>(true);

  useEffect(() => {
    dispatch(fetchNfWorkflow(configState.workflowId))
      .then((nfWorkflow) => {
        const workflowParameters = nfWorkflow.parameters;
        let nfPlaybookParams: NfParamConfiguration[] = [];
        // TODO take the nfWorkflow as the basis (like in PlaybookConfigNfActivitiesConfigScreen), and reorder as a second step
        if (configState.nfParams) {
          // overwrite some properties is needed, in case the original workflow params have mutated
          nfPlaybookParams = configState.nfParams
            .filter((editedNfPlaybookParam) => {
              // filter out the existing configured param in case the Workflow does not have this param anymore
              return workflowParameters.find((wp) => wp.identifier === editedNfPlaybookParam.identifier);
            })
            .map((editedNfPlaybookParam) => {
              const nfWorkflowParam = workflowParameters.find(
                (wp) => wp.identifier === editedNfPlaybookParam.identifier,
              );
              // flesh out the param with the stored configuration
              const nfPlaybookParam: NfParamConfiguration = {
                ...editedNfPlaybookParam,
                id: nfWorkflowParam.identifier,
                // Note that defaultValue is a list!
                defaultWorkflowValue: nfWorkflowParam.defaultValue?.join(", "),
              };
              return nfPlaybookParam;
            });
          // get all params that are not part of the config (either because hidden or added recently to the workflow)
          const additionalParams = differenceBy(workflowParameters, nfPlaybookParams, "identifier");
          // in case of existing configuration hide the unknown params by default unless it is mandatory
          const additionalNfPlaybookParams: NfParamConfiguration[] = additionalParams
            .map(fleshOutWorkflowParamConfiguration)
            .map((nfParamConfig) => ({ ...nfParamConfig, hidden: !nfParamConfig.mandatory }));
          nfPlaybookParams = nfPlaybookParams.concat(additionalNfPlaybookParams);
        } else {
          // show all by default if there is no existing configuration
          nfPlaybookParams = workflowParameters.map(fleshOutWorkflowParamConfiguration);
        }

        dispatch(updatePlaybookConfigState({ nfWorkflow: nfWorkflow, nfParams: nfPlaybookParams }));
      })
      .then(() => {
        if (configState.playbookType === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES) {
          dispatch(fetchFullSampleTypeWithAttributeDefaults(configState.sampleTypeId)).then(() => {
            setLoadingData(false);
          });
        } else {
          setLoadingData(false);
        }
      })
      .catch((error: Error) => {
        props.updateWizardMessage(error.message);
        setLoadingData(false);
      });
  }, []);

  const updateNfParams = (configs) => {
    props.updateWizardMessage(null); // reset any validation error
    dispatch(updatePlaybookConfigState({ nfParams: configs }));
  };

  const isLastScreen: boolean =
    (configState.playbookType === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS && !hpcEnabled && !cloudEnabled) ||
    configState.playbookType === PLAYBOOK_TYPE.REPORT_FROM_COORDINATES ||
    configState.playbookType === PLAYBOOK_TYPE.REPORT_FROM_VARIANTS;

  const handleSubmitData = () => {
    // validation
    const missingType: boolean = configState.nfParams
      .filter((nfParam) => !nfParam.hidden)
      .some((nfParam) => nfParam.type == undefined);
    if (missingType) {
      props.updateWizardMessage("Please configure a specific input type for every workflow parameter of the Playbook");
      return;
    }
    if (configState.playbookType === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES) {
      const rawDataInputs: NfParamConfiguration[] = configState.nfParams
        ?.filter((nfParam) => !nfParam.hidden && nfParam.type.type === NfParamTypeEnum.File)
        .filter(isRawDataFileTypeNfParam);

      // we need at least one valid file parameter
      if (rawDataInputs.length === 0) {
        const possibleParamSubTypes = [
          NfParamSubTypeEnum.Aligned,
          NfParamSubTypeEnum.Unaligned,
          NfParamSubTypeEnum.UnalignedPaired,
          NfParamSubTypeEnum.Unknown, // any file or folder
        ];
        props.updateWizardMessage?.(
          `For this Playbook type, you must select ${rawDataInputs.length > 1 ? "only " : ""}one of the following inputs:
          ${possibleParamSubTypes
            .map((subType: NfParamSubTypeEnum) => getParamTypeLabel({ type: NfParamTypeEnum.File, subType }))
            .join(", ")}.`,
        );
        return;
      }
    }

    if (isLastScreen) {
      props.updateWizardMessage?.(null);
      setSavingPlaybook(true);
      return dispatch(savePlaybook())
        .then(() => {
          setSavingPlaybook(false);
          return props.proceedWithNextStep();
        })
        .catch((error: Error) => {
          props.updateWizardMessage?.(error.message);
          setSavingPlaybook(false);
        });
    } else {
      return props.proceedWithNextStep();
    }
  };

  return (
    <PlaybookWizardStep
      {...props}
      playbookPage={{
        ...props.playbookPage,
        description: getPageDescription(configState.playbookType),
      }}
      wizardFooterData={{
        nextButtonProps: {
          label: isLastScreen ? "Save Playbook" : "Next",
          isDisabled: !!props.wizardErrorMessage || (isLastScreen ? false : false) /* this.props.isLoadingSampleType */,
          isLoading: isLoadingData || (isLastScreen ? isSavingPlaybook : false),
          onPress: handleSubmitData,
        },
      }}
    >
      {isLoadingData ? (
        <LoadingSpinner />
      ) : (
        <NfParamsConfigurator
          // nfWorkflow={configState.nfWorkflow}
          nfParamConfigurations={configState.nfParams ?? []}
          setNfParamConfigurations={updateNfParams}
        />
      )}
    </PlaybookWizardStep>
  );
};

const fleshOutWorkflowParamConfiguration = (nfWorkflowParam: NfWorkflowParam): NfParamConfiguration => {
  const nfPlaybookParam: NfParamConfiguration = {
    id: nfWorkflowParam.identifier,
    identifier: nfWorkflowParam.identifier,
    type: nfWorkflowParam.type,
    label: nfWorkflowParam.identifier,
    hidden: false,
    description: "",
    mandatory: !nfWorkflowParam.defaultValue,
    defaultWorkflowValue: nfWorkflowParam.defaultValue?.join(", "),
    values: [],
  };
  return nfPlaybookParam;
};

const getPageDescription = (playbookType: PLAYBOOK_TYPE) => {
  switch (playbookType) {
    case PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS:
    case PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES: {
      return `Select the workflow parameters that the users will provide during Playbook execution.`;
    }
  }
};

export default PlaybookConfigNfWorkflowInputScreen;
